import type { AgentField, CodeLists } from './screenIndex';
import { codeListFor } from './screenIndex';
import { spokenValue } from './spoken';

/** 사람이 말했는데 화면 목록에 없는 값. */
export type Unlisted = {
  key: string; label: string; said: string; options: string[];
  /** 계획이 대신 넣은 값. 비어 있으면 아예 안 냈다는 뜻이다. */
  put: string;
};

/**
 * **사용자가 말한 값을 계획이 조용히 빼지 못하게 한다.**
 *
 * 실측(2026-09-01): `직위는 통제관` 이라고 시켰더니 계획에 `rspcd` 칸이 아예 없었고,
 * 답변도 직위를 말하지 않았다. 모델은 목록(`TB_PZ001`, 22개)을 받았고 거기 없는
 * 값이라 뺀 것이다 — 판단은 옳지만 **말하지 않은 것이 틀렸다.** 사람은 직위가
 * 들어간 줄 알고 저장한다.
 *
 * 목록이 있는 칸만 본다. 목록이 없으면 없다고 말할 근거가 없다.
 *
 * 계획이 **사용자가 말한 그 값을** 냈으면 건드리지 않는다. 넣다가 걸리는 것은 초안
 * 실행이 이미 후보를 들고 되묻는 길이 있고, 두 번 물으면 안 된다.
 *
 * 그런데 계획이 **다른 값**을 낸 자리는 아무도 안 본다. 실측(2026-09-01, 민원접수):
 * `민원구분은 소음` 이라 했는데 목록에 소음이 없자 계획이 `civilcd: "001"`(고객불만)을
 * 냈다. 목록 안의 값이라 어느 검증도 안 걸리고, 화면에는 **사용자가 말한 적 없는
 * 민원구분**이 남는다. 버리는 것보다 나쁘다 — 버리면 빈칸이라도 눈에 띈다.
 */
export function unlistedSpoken(
  fields: Record<string, string>,
  draftFields: AgentField[],
  question: string,
  lists?: CodeLists,
): Unlisted | undefined {
  for (const field of draftFields) {
    const label = field.label?.trim();
    if (!label) continue;
    const items = codeListFor(field, lists)?.items;
    if (!items?.length) continue;
    const said = spokenValue(question, label);
    if (!said) continue;
    // 계획이 사용자가 말한 그 값을 냈다 — 넣다가 걸리면 초안 실행이 후보를 내민다.
    const put = String(fields[field.key] ?? '').trim();
    if (put === said) continue;
    const names = items.map((item) => String(item.name ?? '').trim()).filter(Boolean);
    // 사람이 코드로 말했으면 그것도 맞는 값이다.
    const codes = items.map((item) => String(item.code ?? '').trim());
    if (names.includes(said) || codes.includes(said)) continue;
    // 부분 일치도 맞는 것으로 본다 — `총무` 라고 말하고 목록은 `총무팀` 인 일이 흔하다.
    // 화면이 고르는 자리는 초안 실행이고, 거기서 후보를 들고 되묻는 길이 이미 있다.
    if (names.some((name) => name.includes(said) || said.includes(name))) continue;
    // **사람에게 코드를 보이지 않는다.** `대신 001 로 넣으려던 것을` 은 아무 말도 아니다.
    const readable = items.find((item) => String(item.code ?? '').trim() === put)?.name;
    return { key: field.key, label, said, options: names, put: String(readable ?? put) };
  }
  return undefined;
}
